import { axiosInstance } from '../clients/axiosClient';
import {
  biomarkerAppointmentsResponse,
  BiomarkerResultType,
  CreateBiomarkerRequest,
  CreateBiomarkerResponse,
  GetExistingBiomarkerResultsByAppointmentResponse,
  HistoricalDataResponse,
  ListBiomarkersResponse,
  ListBiomarkerWithResultsResponse,
  ListResultsByAppointmentResponse,
  SetBiomarkerResultStatusRequest,
  SetBiomarkerResultStatusResponse,
  UpdateBiomarkerRequest,
  UpdateBiomarkerResponse,
  UpdateBiomarkerStatusRequest,
  UpdateBiomarkerStatusResponse
} from '../types/apiGeneratedTypes';
import { BIOMARKERS_API_PREFIX, API_V1_PREFIX } from '../utils/constants';

export const getBiomarkersList = async (
  search?: string,
  sort_by?: string,
  sort_direction?: string,
  include_inactive?: boolean
): Promise<ListBiomarkersResponse> => {
  try {
    const params: Record<string, string> = {};

    if (search) { params.name = search; }
    if (sort_by) { params.sort_field = sort_by; }
    if (sort_direction) { params.sort_order = sort_direction; }
    if (include_inactive) { params.include_inactive = 'true'; }

    const { data } = await axiosInstance.get(
      `${API_V1_PREFIX}${BIOMARKERS_API_PREFIX}list`,
      { params }
    );

    return data;
  } catch (error: any) {
    throw new Error(error?.message || 'Failed to fetch biomarkers list');
  }
};

export const createBiomarker = async (
  newBiomarkerRequestObject: CreateBiomarkerRequest
): Promise<CreateBiomarkerResponse> => {
  try {
    const { data } = await axiosInstance.post(
      `${API_V1_PREFIX}${BIOMARKERS_API_PREFIX}`,
      newBiomarkerRequestObject
    );

    return data;
  } catch (error: any) {
    if (error.response?.data?.description) {
      throw new Error(error.response.data.description);
    }
    throw new Error(error);
  }
};

export const updateBiomarkerStatus = async (
  request: UpdateBiomarkerStatusRequest
): Promise<UpdateBiomarkerStatusResponse> => {
  try {
    const { data } = await axiosInstance.patch(
      `${API_V1_PREFIX}${BIOMARKERS_API_PREFIX}status`,
      request
    );

    return data;
  } catch (error: any) {
    if (error.response?.data?.detail) {
      throw new Error(
        typeof error.response.data.detail === 'string'
          ? error.response.data.detail
          : error.response.data.description
      );
    }
    throw new Error(error?.message || 'Failed to update biomarker status');
  }
};

export const updateBiomarker = async (
  biomarkerRequestObject: UpdateBiomarkerRequest
): Promise<UpdateBiomarkerResponse> => {
  try {
    const { data } = await axiosInstance.patch(
      `${API_V1_PREFIX}${BIOMARKERS_API_PREFIX}`,
      biomarkerRequestObject
    );

    return data;
  } catch (error: any) {
    if (error.response?.data?.description) {
      throw new Error(error.response.data.description);
    }
    throw new Error(error);
  }
};

export const deleteBiomarker = async (id: number): Promise<any> => {
  try {
    await axiosInstance.delete(`${API_V1_PREFIX}${BIOMARKERS_API_PREFIX}`, {
      data: {
        id
      }
    });
    return { success: true };
  } catch (error: any) {
    if (error.response?.data?.description) {
      throw new Error(error.response.data.description);
    }
    throw new Error('Failed to delete biomarker');
  }
};

// export const getBiomarkersResultsList =
//   async (): Promise<GetUnprocessedBiomarkerResultsResponse> => {
//     try {
//       const { data } = await axiosInstance.get(
//         `${API_V1_PREFIX}${BIOMARKERS_API_PREFIX}result/unprocessed`
//       );

//       return data;
//     } catch (error: any) {
//       throw new Error(error);
//     }
//   };
export const getBiomarkersResultsByAppointment = async (
  appointment_id: number,
  result_type: BiomarkerResultType
): Promise<ListResultsByAppointmentResponse> => {
  try {
    const { data } = await axiosInstance.get(
      `${API_V1_PREFIX}${BIOMARKERS_API_PREFIX}result/by-appointment`,
      {
        params: {
          appointment_id,
          result_type
        }
      }
    );

    return data;
  } catch (error: any) {
    throw error;
  }
};
export const getBiomarkersIdsExistingByAppointment = async (
  appointment_id: number
): Promise<GetExistingBiomarkerResultsByAppointmentResponse> => {
  try {
    const { data } = await axiosInstance.get(
      `${API_V1_PREFIX}${BIOMARKERS_API_PREFIX}result/existing-by-appointment`,
      {
        params: {
          appointment_id
        }
      }
    );

    return data;
  } catch (error: any) {
    throw new Error(error);
  }
};
export const getBiomarkersResultsByAppointmentHistoricalData = async (
  biomarker_id: number,
  member_id: string
): Promise<HistoricalDataResponse> => {
  try {
    const { data } = await axiosInstance.get(
      `${API_V1_PREFIX}${BIOMARKERS_API_PREFIX}result/historical-data`,
      {
        params: {
          biomarker_id,
          member_id
        }
      }
    );

    return data;
  } catch (error: any) {
    throw new Error(error);
  }
};
export const getMemberBiomarkersResults = async (
  member_id: string,
  search?: string,
  sort_by?: string,
  sort_direction?: string,
  appointmentDate?: string,
  bodySystem?: string
): Promise<ListBiomarkerWithResultsResponse> => {
  try {
    const params: Record<string, string> = {
      member_id,
    };

    if (search) { params.biomarker_name = search; }
    if (sort_by) { params.sort_field = sort_by; }
    if (sort_direction) { params.sort_order = sort_direction; }
    if (appointmentDate) { params.appointment_date = appointmentDate; }
    if (bodySystem) { params.body_system_name = bodySystem; }

    const { data } = await axiosInstance.get(
      `${API_V1_PREFIX}${BIOMARKERS_API_PREFIX}result`,
      { params }
    );

    return data;
  } catch (error: any) {
    throw new Error(error?.message || 'Failed to fetch biomarker results');
  }
};

export const setBiomarkerResultStatus = async (
  biomarkerResultStatusRequest: SetBiomarkerResultStatusRequest
): Promise<SetBiomarkerResultStatusResponse> => {
  try {
    const { data } = await axiosInstance.patch(
      `${API_V1_PREFIX}${BIOMARKERS_API_PREFIX}result/set-status`,
      biomarkerResultStatusRequest
    );

    return data;
  } catch (error: any) {
    throw error;
  }
};
export const deleteBiomarkerResult = async (
  result_ids: number[]
): Promise<any> => {
  try {
    await axiosInstance.delete(
      `${API_V1_PREFIX}${BIOMARKERS_API_PREFIX}result`,
      {
        data: {
          result_ids
        }
      }
    );
  } catch (error: any) {
    throw error;
  }
};

export const getBiomarkerAppointments = async (
  limit: number,
  offset: number,
  member_id: string,
): Promise<biomarkerAppointmentsResponse> => {
  try {
    const params: Record<string, string> = {
      member_id,
      limit: limit.toString(),
      offset: offset.toString()
    };

    const { data } = await axiosInstance.get(
      `${API_V1_PREFIX}${BIOMARKERS_API_PREFIX}result/appointment-dates`,
      { params }
    );

    return data;
  } catch (error: any) {
    throw new Error(error?.message || 'Failed to fetch biomarker results');
  }
};